import { useState, useEffect } from 'react'
import { useConfig } from '@/modules/config/hooks/useConfig'
import { useConfigMutation } from '@/modules/config/hooks/useConfigMutation'
import { ProductForm } from '@/modules/config/components/ProductForm'
import { ProductList } from '@/modules/config/components/ProductList'
import { ErrorState } from '@/shared/components/ui/atoms/ErrorState'
import type { Product, ProductFormData } from '@/modules/config/types'

export function ProductsPage() {
  const { data, isLoading, error, refetch } = useConfig()
  const { updateProducts, isSubmitting, error: mutationError, success } = useConfigMutation()
  const [products, setProducts] = useState<Product[]>([])
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (data) {
      setProducts(data.products ?? [])
    }
  }, [data])

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-3xl">
        <div className="animate-pulse h-8 w-48 bg-gray-100 rounded" />
        <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
        <div className="animate-pulse h-64 bg-gray-100 rounded-lg" />
      </div>
    )
  }

  if (error) {
    return <ErrorState message={error} onRetry={refetch} />
  }

  const handleAddProduct = async (formData: ProductFormData) => {
    setFormError(null)

    const skuExists = products.some((p) => p.sku === formData.sku)
    if (skuExists) {
      setFormError('Error: SKU duplicado. El SKU ya existe en la lista de productos.')
      return
    }

    const updatedProducts = [...products, formData]
    await updateProducts(updatedProducts)
    setProducts(updatedProducts)
  }

  const handleDeleteProduct = async (sku: string) => {
    const updatedProducts = products.filter((p) => p.sku !== sku)
    await updateProducts(updatedProducts)
    setProducts(updatedProducts)
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Productos</h1>

      {/* Feedback messages */}
      {mutationError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {mutationError}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Add Product Form */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Ingresar nuevo producto</h2>
        <ProductForm
          onSubmit={handleAddProduct}
          isSubmitting={isSubmitting}
          error={formError}
        />
      </div>

      {/* Product List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Listado de productos</h2>
        <ProductList
          products={products}
          onDelete={handleDeleteProduct}
        />
      </div>
    </div>
  )
}
